import React from "react";
import { parseErrorMessage } from "@/lib/parseErrorMessage";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const PREDEFINED_TAGS = [
  { key: "influencer", label: "Influencer" },
  { key: "product", label: "Product" },
  { key: "offer", label: "Offer" },
  { key: "concept", label: "Concept" },
];

interface BulkTagEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: Set<string>;
  onSuccess?: () => void;
}

export function BulkTagEditor({ open, onOpenChange, selectedIds, onSuccess }: BulkTagEditorProps) {
  const { toast } = useToast();
  const [tags, setTags] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const handleApply = async () => {
    // Only apply non-empty tags
    const toApply: Record<string, string> = {};
    for (const [k, v] of Object.entries(tags)) {
      if (v && v.trim()) toApply[k] = v.trim();
    }

    if (Object.keys(toApply).length === 0) {
      toast({ title: "No tags to apply", description: "Enter at least one tag value" });
      return;
    }

    setIsSaving(true);
    try {
      // Use JSONB merge operator via RPC or direct update per row
      const ids = Array.from(selectedIds);
      
      // Supabase JS doesn't support || operator directly, so we fetch + merge
      const { data: existing, error: fetchError } = await supabase
        .from("creative_metadata")
        .select("id, tags")
        .in("id", ids);

      if (fetchError) throw fetchError;

      // Update each row with merged tags
      for (const row of existing || []) {
        const existingTags = (row.tags as Record<string, string>) || {};
        const mergedTags = { ...existingTags, ...toApply };
        
        const { error } = await supabase
          .from("creative_metadata")
          .update({ tags: mergedTags })
          .eq("id", row.id);

        if (error) throw error;
      }

      toast({ title: "Tags applied", description: `Updated ${ids.length} creative${ids.length !== 1 ? "s" : ""}` });
      setTags({});
      onOpenChange(false);
      onSuccess?.();
    } catch (err: unknown) {
      toast({ title: "Failed to apply tags", description: parseErrorMessage(err), variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Tags ({selectedIds.size} creatives)</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-xs text-muted-foreground">
            Enter tag values to apply. Empty fields will be skipped. Existing tags are preserved.
          </p>

          {PREDEFINED_TAGS.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground w-24 shrink-0">{label}</span>
              <Input
                value={tags[key] || ""}
                onChange={(e) => setTags(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={`Enter ${label.toLowerCase()}...`}
                className="h-8 text-sm"
              />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={isSaving}>
            {isSaving ? "Applying..." : "Apply Tags"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
